
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ConfigProvider } from './context/ConfigContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { TerminologyProvider } from './context/TerminologyContext'; 
import { PublicLayout } from './components/Layout';
import { Loader2 } from 'lucide-react';
import { SEO } from './components/SEO';
import { FeatureGuard } from './components/auth/FeatureGuard'; 
import { getAppMode, isPublicPagesDisabledByDefault } from './utils/config'; 
import { getModuleSettings } from './services/dataService'; 

import { ErrorBoundary } from './components/ErrorBoundary';

// Helper for dynamic imports with resilient retry and stale chunk auto-recovery
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T } | any>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const module = await factory();
      return { default: module.default || module };
    } catch (error: any) {
      console.warn("Retrying lazy load for module...", error);
      await new Promise(r => setTimeout(r, 300));
      try {
        const module = await factory();
        return { default: module.default || module };
      } catch (retryError: any) {
        const msg = String(retryError?.message || retryError);
        const isDynamicImportError = 
          msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('Importing a module script failed') ||
          msg.includes('error loading dynamically imported module');
        if (isDynamicImportError && typeof window !== 'undefined') {
          const last = (window as any).__lastChunkReload || 0;
          if (Date.now() - last > 15000) {
            (window as any).__lastChunkReload = Date.now();
            window.location.reload();
          }
        }
        throw retryError;
      }
    }
  });
}

// Global handler for Vite preloading failures
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('Vite preload error detected, refreshing page with latest bundle...', event);
    window.location.reload();
  });
}

// --- Lazy Load Pages ---
const ProtectedRoute = lazyWithRetry(() => import('./modules/auth/components/ProtectedRoute').then(module => ({ default: module.ProtectedRoute })));

// Auth
const Login = lazyWithRetry(() => import('./modules/auth/pages/Login').then(m => ({ default: m.Login })));
const StaffLogin = lazyWithRetry(() => import('./modules/auth/pages/StaffLogin').then(m => ({ default: m.StaffLogin })));
const SSOCallback = lazyWithRetry(() => import('./modules/auth/pages/SSOCallback').then(m => ({ default: m.SSOCallback })));
const OAuthConsentWizard = lazyWithRetry(() => import('./modules/auth/pages/OAuthConsentWizard').then(m => ({ default: m.OAuthConsentWizard })));
const OAuthCallback = lazyWithRetry(() => import('./modules/auth/pages/OAuthCallback').then(m => ({ default: m.OAuthCallback })));

// Admin
const SuperAdminDashboard = lazyWithRetry(() => import('./modules/saas/pages/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const SaaSRegister = lazyWithRetry(() => import('./modules/saas/pages/SaaSRegister').then(m => ({ default: m.SaaSRegister })));

// Public
const PortfolioLanding = lazyWithRetry(() => import('./modules/core/pages/PortfolioLanding').then(m => ({ default: m.PortfolioLanding })));
const About = lazyWithRetry(() => import('./modules/core/pages/About').then(m => ({ default: m.About }))); 
const ServicesPage = lazyWithRetry(() => import('./modules/services/pages/PublicServices').then(m => ({ default: m.ServicesPage })));
const WorkshopPage = lazyWithRetry(() => import('./modules/workshop/pages/WorkshopPage').then(m => ({ default: m.WorkshopPage })));
const Contact = lazyWithRetry(() => import('./modules/contact/pages/Contact').then(m => ({ default: m.Contact })));
const Portfolio = lazyWithRetry(() => import('./modules/portfolio/pages/PortfolioGrid').then(m => ({ default: m.PortfolioGrid })));
const PortfolioDetail = lazyWithRetry(() => import('./modules/portfolio/pages/PortfolioDetail').then(m => ({ default: m.PortfolioDetail })));
const PublicCV = lazyWithRetry(() => import('./modules/portfolio/pages/PublicCV').then(m => ({ default: m.PublicCV })));
const Marketplace = lazyWithRetry(() => import('./modules/marketplace/pages/Marketplace').then(m => ({ default: m.default || m.Marketplace })));
const NotFound = lazyWithRetry(() => import('./modules/core/pages/NotFound').then(m => ({ default: m.NotFound })));

// Legal
const Privacy = lazyWithRetry(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.Privacy })));
const Terms = lazyWithRetry(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.Terms })));
const RefundPolicy = lazyWithRetry(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.RefundPolicy })));
const EULA = lazyWithRetry(() => import('./modules/legal/pages/LegalDocs').then(m => ({ default: m.EULA })));

// Booking Engines
const QuoteRequest = lazyWithRetry(() => import('./modules/orders/pages/QuoteRequest').then(m => ({ default: m.QuoteRequest })));
const ServiceBooking = lazyWithRetry(() => import('./modules/bookings/pages/BookingEngine').then(m => ({ default: m.ServiceBooking })));
const ServiceOrder = lazyWithRetry(() => import('./modules/orders/pages/ServiceOrder').then(m => ({ default: m.ServiceOrder })));
const CheckoutSuccess = lazyWithRetry(() => import('./modules/orders/pages/CheckoutSuccess').then(m => ({ default: m.CheckoutSuccess })));

// Dashboard
const Overview = lazyWithRetry(() => import('./modules/core/pages/Overview').then(m => ({ default: m.Overview })));
const Bookings = lazyWithRetry(() => import('./modules/bookings/pages/BookingsList').then(m => ({ default: m.Bookings })));
const Schedule = lazyWithRetry(() => import('./modules/bookings/pages/Availability').then(m => ({ default: m.Schedule })));
const Orders = lazyWithRetry(() => import('./modules/orders/pages/Orders').then(m => ({ default: m.Orders })));
const Files = lazyWithRetry(() => import('./modules/core/pages/Files').then(m => ({ default: m.Files })));
const Clients = lazyWithRetry(() => import('./modules/core/pages/Clients').then(m => ({ default: m.Clients })));
const Team = lazyWithRetry(() => import('./modules/core/pages/Team').then(m => ({ default: m.Team })));
const Settings = lazyWithRetry(() => import('./modules/core/pages/Settings').then(m => ({ default: m.Settings })));
const ClientServices = lazyWithRetry(() => import('./modules/services/pages/ServiceCatalog').then(m => ({ default: m.ClientServices })));
const ServicesManager = lazyWithRetry(() => import('./modules/services/pages/ServicesManager').then(m => ({ default: m.ServicesManager })));
const WorkshopManager = lazyWithRetry(() => import('./modules/workshop/pages/WorkshopManager').then(m => ({ default: m.WorkshopManager })));
const Finances = lazyWithRetry(() => import('./modules/finance/pages/Finances').then(m => ({ default: m.Finances })));
const ContractTemplates = lazyWithRetry(() => import('./modules/legal/pages/ContractTemplates').then(m => ({ default: m.ContractTemplates })));
const PortfolioSettings = lazyWithRetry(() => import('./modules/portfolio/pages/PortfolioSettings').then(m => ({ default: m.PortfolioSettings })));
const CVEditor = lazyWithRetry(() => import('./modules/portfolio/pages/CVEditor').then(m => ({ default: m.CVEditor })));
const Newsletters = lazyWithRetry(() => import('./modules/marketing/pages/Newsletters').then(m => ({ default: m.Newsletters })));
const FormManager = lazyWithRetry(() => import('./modules/forms/pages/FormManager').then(m => ({ default: m.FormManager })));
const ProposalsList = lazyWithRetry(() => import('./modules/proposals/pages/ProposalsList').then(m => ({ default: m.ProposalsList })));
const ProposalView = lazyWithRetry(() => import('./modules/proposals/pages/ProposalView').then(m => ({ default: m.ProposalView })));
const Pipelines = lazyWithRetry(() => import('./modules/orders/pages/Pipelines').then(m => ({ default: m.Pipelines })));
const RequirementsWizard = lazyWithRetry(() => import('./modules/requirements/pages/RequirementsWizard').then(m => ({ default: m.RequirementsWizard })));
const RequirementsList = lazyWithRetry(() => import('./modules/requirements/pages/RequirementsList').then(m => ({ default: m.RequirementsList })));

const PageLoader = () => (
  <div className="h-screen flex items-center justify-center bg-black text-neutral-500 font-mono text-xs">
    <Loader2 className="animate-spin mr-2" /> LOADING...
  </div>
);

// 1-Toggle Public Portal Access Guard
// When disablePublicPages is true:
// - Unauthenticated visitors are locked into the Login page (Login Only mode).
// - Authenticated users visiting public URLs are redirected to /dashboard.
const PublicRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { tenant } = useTenant();
  const [publicDisabled, setPublicDisabled] = useState<boolean>(() => isPublicPagesDisabledByDefault());

  useEffect(() => {
    const handleUpdate = (e: any) => {
      if (e?.detail?.disablePublicPages !== undefined) {
        setPublicDisabled(Boolean(e.detail.disablePublicPages));
      }
    };
    window.addEventListener('syncz_modules_updated', handleUpdate);
    window.addEventListener('syncz_platform_updated', handleUpdate);

    getModuleSettings(tenant?.id).then(m => {
      if (m && m.disablePublicPages !== undefined) {
        setPublicDisabled(Boolean(m.disablePublicPages));
      }
    });

    return () => {
      window.removeEventListener('syncz_modules_updated', handleUpdate);
      window.removeEventListener('syncz_platform_updated', handleUpdate);
    };
  }, [tenant?.id]);

  if (publicDisabled) {
    if (!user && !loading) {
      return (
        <PublicLayout>
          <Login />
        </PublicLayout>
      );
    }
    if (user && !loading) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Root Router
const RootRoute = () => {
  const [appMode, setAppMode] = useState<'PORTFOLIO' | 'MARKETPLACE' | 'PORTAL'>(() => getAppMode());

  useEffect(() => {
    const handleModeUpdate = () => {
      setAppMode(getAppMode());
    };
    window.addEventListener('syncz_marketplace_updated', handleModeUpdate);
    window.addEventListener('syncz_platform_updated', handleModeUpdate);
    return () => {
      window.removeEventListener('syncz_marketplace_updated', handleModeUpdate);
      window.removeEventListener('syncz_platform_updated', handleModeUpdate);
    };
  }, []);

  return (
    <PublicRouteGuard>
      <PublicLayout>
        <div className="relative">
          {appMode === 'MARKETPLACE' ? <Marketplace /> : <PortfolioLanding />}
        </div>
      </PublicLayout>
    </PublicRouteGuard>
  );
};

const DashboardRoutes = () => (
    <>
        <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
        
        {/* Gated Modules */}
        <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
        <Route path="proposals" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalsList /></FeatureGuard></ProtectedRoute>} />
        <Route path="proposals/:id" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalView /></FeatureGuard></ProtectedRoute>} />
        <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
        <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
        <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
        <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
        <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
        <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
        <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
        <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
        <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
        <Route path="portfolio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><PortfolioSettings /></ProtectedRoute>} />
        <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
        <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
        <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
        <Route path="requirements" element={<ProtectedRoute><RequirementsList /></ProtectedRoute>} />
        <Route path="requirements/new" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
        <Route path="requirements/:id" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
    </>
);

const PublicRoutes = () => (
    <>
        <Route path="about" element={<PublicRouteGuard><PublicLayout><About /></PublicLayout></PublicRouteGuard>} />
        <Route path="services" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
        <Route path="services/portfolio/:portfolioId" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
        {/* NEW: Custom Slug Route for Artists/Owners */}
        <Route path="owner/:slug" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
        
        <Route path="workshops" element={<PublicRouteGuard><PublicLayout><WorkshopPage /></PublicLayout></PublicRouteGuard>} />
        <Route path="portfolio" element={<PublicRouteGuard><PublicLayout><Portfolio /></PublicLayout></PublicRouteGuard>} />
        <Route path="portfolio/:id" element={<PublicRouteGuard><PublicLayout><PortfolioDetail /></PublicLayout></PublicRouteGuard>} />
        <Route path="cv" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
        <Route path="cv/:id" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
        <Route path="proposal/:id" element={<ProposalView />} />
        <Route path="contact" element={<PublicRouteGuard><PublicLayout><Contact /></PublicLayout></PublicRouteGuard>} />
        
        {/* Legal */}
        <Route path="privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
        <Route path="terms" element={<PublicLayout><Terms /></PublicLayout>} />
        <Route path="refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
        <Route path="eula" element={<PublicLayout><EULA /></PublicLayout>} />

        {/* Engines */}
        <Route path="quote/:serviceId" element={<PublicRouteGuard><PublicLayout><QuoteRequest /></PublicLayout></PublicRouteGuard>} />
        <Route path="book/:id" element={<PublicRouteGuard><PublicLayout><ServiceBooking /></PublicLayout></PublicRouteGuard>} />
        <Route path="order/:id" element={<PublicRouteGuard><PublicLayout><ServiceOrder /></PublicLayout></PublicRouteGuard>} />
        
        {/* Auth */}
        <Route path="login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />
    </>
);

const AppRoutes = () => {
    return (
        <Routes>
            {/* 1. Global / Subdomain Routes */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/marketplace" element={<PublicLayout><SEO title="Marketplace" /><Marketplace /></PublicLayout>} />
            
            <Route path="/admin" element={<StaffLogin />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            <Route path="/oauth/authorize" element={<OAuthConsentWizard />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/api/auth/callback" element={<OAuthCallback />} />
            
            {/* SaaS Admin */}
            <Route path="/saas-admin" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN']}><SuperAdminDashboard /></ProtectedRoute>} />
            
            {/* Shared Public Routes (Root based) */}
            <Route path="/about" element={<PublicRouteGuard><PublicLayout><About /></PublicLayout></PublicRouteGuard>} />
            <Route path="/services" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
            <Route path="/services/portfolio/:portfolioId" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
            <Route path="/owner/:slug" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
            
            <Route path="/workshops" element={<PublicRouteGuard><PublicLayout><WorkshopPage /></PublicLayout></PublicRouteGuard>} />
            <Route path="/portfolio" element={<PublicRouteGuard><PublicLayout><Portfolio /></PublicLayout></PublicRouteGuard>} />
            <Route path="/portfolio/:id" element={<PublicRouteGuard><PublicLayout><PortfolioDetail /></PublicLayout></PublicRouteGuard>} />
            <Route path="/cv" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
            <Route path="/cv/:id" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
            <Route path="/contact" element={<PublicRouteGuard><PublicLayout><Contact /></PublicLayout></PublicRouteGuard>} />
            <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
            <Route path="/refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
            <Route path="/eula" element={<PublicLayout><EULA /></PublicLayout>} />
            <Route path="/quote/:serviceId" element={<PublicRouteGuard><PublicLayout><QuoteRequest /></PublicLayout></PublicRouteGuard>} />
            <Route path="/book/:id" element={<PublicRouteGuard><PublicLayout><ServiceBooking /></PublicLayout></PublicRouteGuard>} />
            <Route path="/order/:id" element={<PublicRouteGuard><PublicLayout><ServiceOrder /></PublicLayout></PublicRouteGuard>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />
            <Route path="/requirements/new" element={<PublicRouteGuard><PublicLayout><RequirementsWizard /></PublicLayout></PublicRouteGuard>} />
            <Route path="/requirements/:id" element={<PublicRouteGuard><PublicLayout><RequirementsWizard /></PublicLayout></PublicRouteGuard>} />
            <Route path="/checkout-success" element={<CheckoutSuccess />} />

            {/* Dashboard (Root based) */}
            <Route path="/dashboard">
                <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
                <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
                <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
                <Route path="proposals" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalsList /></FeatureGuard></ProtectedRoute>} />
                <Route path="proposals/:id" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalView /></FeatureGuard></ProtectedRoute>} />
                <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
                <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
                <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
                <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
                <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
                <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
                <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
                <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
                <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
                <Route path="portfolio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><PortfolioSettings /></ProtectedRoute>} />
                <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
                <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
                <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
                <Route path="pipelines" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><Pipelines /></FeatureGuard></ProtectedRoute>} />
                <Route path="requirements" element={<ProtectedRoute><RequirementsList /></ProtectedRoute>} />
                <Route path="requirements/new" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
                <Route path="requirements/:id" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
            </Route>

            {/* 2. Path-Based Tenant Routes ( /t/:tenantSlug/... ) */}
            <Route path="/t/:tenantSlug">
                <Route index element={<PublicRouteGuard><PublicLayout><PortfolioLanding /></PublicLayout></PublicRouteGuard>} />
                
                {/* Replicate Public Routes under /t/:tenantSlug */}
                <Route path="about" element={<PublicRouteGuard><PublicLayout><About /></PublicLayout></PublicRouteGuard>} />
                <Route path="services" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
                <Route path="owner/:slug" element={<PublicRouteGuard><PublicLayout><ServicesPage /></PublicLayout></PublicRouteGuard>} />
                <Route path="workshops" element={<PublicRouteGuard><PublicLayout><WorkshopPage /></PublicLayout></PublicRouteGuard>} />
                <Route path="portfolio" element={<PublicRouteGuard><PublicLayout><Portfolio /></PublicLayout></PublicRouteGuard>} />
                <Route path="portfolio/:id" element={<PublicRouteGuard><PublicLayout><PortfolioDetail /></PublicLayout></PublicRouteGuard>} />
                <Route path="cv" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
                <Route path="cv/:id" element={<PublicRouteGuard><PublicLayout><PublicCV /></PublicLayout></PublicRouteGuard>} />
                <Route path="proposal/:id" element={<ProposalView />} />
                <Route path="contact" element={<PublicRouteGuard><PublicLayout><Contact /></PublicLayout></PublicRouteGuard>} />
                <Route path="privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
                <Route path="terms" element={<PublicLayout><Terms /></PublicLayout>} />
                <Route path="refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
                <Route path="eula" element={<PublicLayout><EULA /></PublicLayout>} />
                <Route path="quote/:serviceId" element={<PublicRouteGuard><PublicLayout><QuoteRequest /></PublicLayout></PublicRouteGuard>} />
                <Route path="book/:id" element={<PublicRouteGuard><PublicLayout><ServiceBooking /></PublicLayout></PublicRouteGuard>} />
                <Route path="order/:id" element={<PublicRouteGuard><PublicLayout><ServiceOrder /></PublicLayout></PublicRouteGuard>} />
                <Route path="login" element={<PublicLayout><Login /></PublicLayout>} />
                <Route path="register" element={<PublicLayout><SaaSRegister /></PublicLayout>} />
                <Route path="requirements/new" element={<PublicRouteGuard><PublicLayout><RequirementsWizard /></PublicLayout></PublicRouteGuard>} />
                <Route path="requirements/:id" element={<PublicRouteGuard><PublicLayout><RequirementsWizard /></PublicLayout></PublicRouteGuard>} />
                
                {/* Replicate Dashboard Routes under /t/:tenantSlug/dashboard */}
                <Route path="dashboard">
                    <Route index element={<ProtectedRoute><Overview /></ProtectedRoute>} />
                    <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
                    <Route path="orders" element={<ProtectedRoute><FeatureGuard feature="projects"><Orders /></FeatureGuard></ProtectedRoute>} />
                    <Route path="proposals" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalsList /></FeatureGuard></ProtectedRoute>} />
                    <Route path="proposals/:id" element={<ProtectedRoute><FeatureGuard feature="projects"><ProposalView /></FeatureGuard></ProtectedRoute>} />
                    <Route path="browse" element={<ProtectedRoute><ClientServices /></ProtectedRoute>} />
                    <Route path="bookings" element={<ProtectedRoute><FeatureGuard feature="bookings"><Bookings /></FeatureGuard></ProtectedRoute>} />
                    <Route path="schedule" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="bookings"><Schedule /></FeatureGuard></ProtectedRoute>} />
                    <Route path="clients" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><Clients /></ProtectedRoute>} />
                    <Route path="services" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><ServicesManager /></FeatureGuard></ProtectedRoute>} />
                    <Route path="workshops" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><WorkshopManager /></ProtectedRoute>} />
                    <Route path="marketing" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="marketing_module"><Newsletters /></FeatureGuard></ProtectedRoute>} />
                    <Route path="contracts" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><ContractTemplates /></ProtectedRoute>} />
                    <Route path="finances" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="finance_module"><Finances /></FeatureGuard></ProtectedRoute>} />
                    <Route path="portfolio" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><PortfolioSettings /></ProtectedRoute>} />
                    <Route path="team" element={<ProtectedRoute allowedRoles={['OWNER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="teams"><Team /></FeatureGuard></ProtectedRoute>} />
                    <Route path="cv-editor" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><CVEditor /></ProtectedRoute>} />
                    <Route path="forms" element={<ProtectedRoute allowedRoles={['OWNER', 'SUPER_ADMIN']}><FeatureGuard feature="forms_module"><FormManager /></FeatureGuard></ProtectedRoute>} />
                    <Route path="pipelines" element={<ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'ARTIST', 'SUPER_ADMIN']}><FeatureGuard feature="projects"><Pipelines /></FeatureGuard></ProtectedRoute>} />
                    <Route path="requirements" element={<ProtectedRoute><RequirementsList /></ProtectedRoute>} />
                    <Route path="requirements/new" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
                    <Route path="requirements/:id" element={<ProtectedRoute><RequirementsWizard /></ProtectedRoute>} />
                </Route>
            </Route>

            <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
    );
};

import { ScrollToTop } from './components/ScrollToTop';
import { BackToTop } from './components/ui/BackToTop';

const App: React.FC = () => {
  return (
    <ConfigProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <TenantProvider>
             <TerminologyProvider>
                <AuthProvider>
                  <NotificationProvider>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                          <AppRoutes />
                      </Suspense>
                    </ErrorBoundary>
                    <BackToTop />
                  </NotificationProvider>
                </AuthProvider>
             </TerminologyProvider>
        </TenantProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
